import { useState, FormEvent, ChangeEvent } from 'react';
import EmployeeService from '../../AppSettings/services/EmployeeService';
import type { CreateFieldMemberRequest } from '../../Models/Employee/EmployeeRequestResponseModels';

/**
 * Add a FIELD MEMBER (plan field-members-and-signin-conversion, Build M): a person who can be
 * assigned to matter teams and phase crew but cannot sign in — no Entra account, no
 * permission. The id is minted server-side (never supplied here — the pre-claim wall), and
 * email/phone are optional: a field hand may have neither. Build C later converts a member to
 * a sign-in user by LINKING an Entra identity to the same row.
 *
 * CreateContactModal's form idiom (overlay + team-form-group inputs), because the two sit on
 * the same screen and must feel like one system.
 */
interface AddFieldMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMemberCreated: () => void;
}

const INITIAL_MEMBER: CreateFieldMemberRequest = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
};

const AddFieldMemberModal = ({
  isOpen,
  onClose,
  onMemberCreated,
}: AddFieldMemberModalProps): JSX.Element | null => {
  const [member, setMember] = useState<CreateFieldMemberRequest>(INITIAL_MEMBER);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const handleCreate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    // Double-submit mints two rows for one human, and members have no delete (decision 7).
    if (submitting) return;
    setSubmitting(true);
    try {
      await EmployeeService.createFieldMember({
        firstName: member.firstName.trim(),
        lastName: member.lastName.trim(),
        // Optional facts stay ABSENT when blank — never empty strings in the record.
        email: member.email?.trim() ? member.email.trim() : null,
        phone: member.phone?.trim() ? member.phone.trim() : null,
      });
      await onMemberCreated();
      setMember(INITIAL_MEMBER);
      setError(null);
      onClose();
    } catch (err) {
      // The rows the admin typed stay on screen for the retry.
      setError((err as Error)?.message || 'Could not add the field member. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="team-new-contact-form-overlay">
      <div className="team-new-contact-form">
        <h3>Add field member</h3>
        <p className="team-field-member-note">
          Field members can be put on project teams and phase crews but cannot sign in.
          Sign-in can be enabled later without losing their history.
        </p>
        <form onSubmit={handleCreate}>
          <div className="team-form-group">
            <input
              type="text"
              placeholder="First name"
              aria-label="First name"
              maxLength={50}
              value={member.firstName}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setMember({ ...member, firstName: e.target.value })
              }
              required
            />
          </div>
          <div className="team-form-group">
            <input
              type="text"
              placeholder="Last name"
              aria-label="Last name"
              maxLength={50}
              value={member.lastName}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setMember({ ...member, lastName: e.target.value })
              }
              required
            />
          </div>
          <div className="team-form-group">
            <input
              type="email"
              placeholder="Email (optional)"
              aria-label="Email (optional)"
              maxLength={100}
              value={member.email ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setMember({ ...member, email: e.target.value })
              }
            />
          </div>
          <div className="team-form-group">
            <input
              type="tel"
              placeholder="Phone (optional)"
              aria-label="Phone (optional)"
              maxLength={20}
              value={member.phone ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setMember({ ...member, phone: e.target.value })
              }
            />
          </div>
          {error && <p className="team-form-error" role="alert">{error}</p>}
          <div className="team-form-actions">
            <button type="submit" className="team-submit-button" disabled={submitting}>
              Add member
            </button>
            <button
              type="button"
              className="team-cancel-button"
              onClick={() => {
                // A refusal must not replay on the NEXT open — role="alert" would announce
                // an error that never happened (pre-PR review).
                setError(null);
                onClose();
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddFieldMemberModal;
